import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, message, Card, Input, Pagination, Alert } from 'antd';
import { EditOutlined, DeleteOutlined, BarsOutlined } from "@ant-design/icons";
import axios from 'axios';
import CareerForm from './careerForm';
import dayjs from 'dayjs';

const Career = () => {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const [categories, setCategories] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [detailData, setDetailData] = useState();
    const [openDetail, setOpenDetail] = useState();
    const [sortField, setSortField] = useState("id");
    const [sortOrder, setSortOrder] = useState("DESC");
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    const fieldLabels = {
        title: "Name",
        location: "Location",
        category: "Category",
        type: "Type",
        description: "Description",
        qualification: "Qualification",
        company: "Company",
        deadline: "Deadline",
        published: "Published",
        created_at: 'Created At',
        updated_at: "Updated At"
    };

    const detailOrder = [
        "title",
        "location",
        "category",
        "type",
        "description",
        "qualification",
        "company",
        "deadline",
        "published",
        "created_at",
        "updated_at"
    ];

    // Map AntD order ke SQL order
    const mapOrder = (order) => {
        if (order === "ascend") return "ASC";
        if (order === "descend") return "DESC";
        return "DESC";
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/category_jobs`);
            setCategories(res.data.data || []);
        } catch {
            message.error('Failed to fetch categories');
        }
    };

    const fetchJobs = async (pageNumber = 1) => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/api/jobs`, {
                params: {
                    search: searchText,
                    page: pageNumber,
                    limit,
                    sort: sortField,
                    order: sortOrder
                }
            });
            setJobs(res.data.data || []);
            setTotal(res.data.total || 0);
            setPage(pageNumber);
        } catch {
            message.error('Failed to fetch jobs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCategories(); }, []);
    useEffect(() => { fetchJobs(page); }, [page, searchText, sortField, sortOrder]);

    const handleSubmit = async (formData) => {
        try {
            if (editData) {
                await axios.put(`${BASE_URL}/api/jobs/${editData.id}`, formData);
                setSuccessMsg('Job updated successfully');
            } else {
                await axios.post(`${BASE_URL}/api/jobs`, formData);
                setSuccessMsg('Job added successfully');
            }
            setTimeout(() => setSuccessMsg(''), 3000);
            setOpen(false);
            setEditData(null);
            fetchJobs(1);
        } catch {
            message.error('Failed to submit job');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${BASE_URL}/api/jobs/${id}`);
            setSuccessMsg('Job deleted successfully');
            setTimeout(() => setSuccessMsg(''), 3000);
            fetchJobs(page);
        } catch {
            message.error('Failed to delete job');
        }
    };

    const jobsWithCategory = jobs.map(job => ({
        ...job,
        categoryName: categories.find(cat => String(cat.id) === String(job.categoryId))?.name || "-"
    }));

    const columns = [
        { title: 'No', render: (_, __, index) => (page - 1) * limit + index + 1 },
        { title: 'Name', dataIndex: 'title', sorter: true },
        { title: 'Location', dataIndex: 'location' },
        { title: 'Type', dataIndex: 'type', sorter: true },
        { title: 'Company', dataIndex: 'company', sorter: true },
        {
            title: 'Deadline',
            dataIndex: 'deadline',
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD") : "-"
        },
        {
            title: 'Actions',
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                    <Button
                        style={{
                            // backgroundColor: '#fff',
                            borderColor: '#ec4899',
                            color: '#ec4899',
                            marginRight: 8,
                        }}
                        icon={<BarsOutlined />}
                        type="link"
                        onClick={() => {
                            setDetailData({
                                ...record,
                                image: [
                                    {
                                        uid: "-1",
                                        name: "image.jpg",
                                        status: "done",
                                        url: `${BASE_URL}${record.image}`,
                                    },
                                ],
                            });
                            setOpenDetail(true);
                        }} />
                    <Button
                        style={{
                            backgroundColor: '#ec4899',
                            borderColor: '#ec4899',
                            color: '#fff',
                            marginRight: 8,
                        }}
                        icon={<EditOutlined />}
                        type="link"
                        onClick={() => {
                            setEditData({
                                ...record,
                                image: [
                                    {
                                        uid: "-1",
                                        name: "image.jpg",
                                        status: "done",
                                        url: `${BASE_URL}${record.image}`,
                                    },
                                ],
                            });
                            setOpen(true);
                        }} />
                    <Button
                        style={{
                            backgroundColor: '#374151',
                            borderColor: '#374151',
                            color: '#fff',
                            marginRight: 8,
                        }}
                        icon={<DeleteOutlined />}
                        type="link"
                        onClick={() => {
                            Modal.confirm({
                                title: 'Are you sure you want to delete this banner?',
                                centered: true,
                                okText: 'Yes',
                                okType: 'danger',
                                cancelText: 'No',
                                onOk: () => handleDelete(record.id),
                            });
                        }} />
                </div>
            ),
        },
    ];

    return (
        <Card title="Jobs">
            {successMsg && (
                <Alert message={successMsg} type="success" showIcon style={{ marginBottom: 16, borderRadius: 8 }} />
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Input.Search
                    placeholder="Search Jobs..."
                    allowClear
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
                <Button
                    style={{ backgroundColor: '#ec4899', borderColor: '#ec4899', color: '#fff' }}
                    onClick={() => { setEditData(null); setOpen(true); }}
                >
                    + Add Jobs
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={jobsWithCategory}
                rowKey="id"
                loading={loading}
                pagination={false}
                onChange={(pagination, filters, sorter) => {
                    if (sorter.order) {
                        setSortField(sorter.field);
                        setSortOrder(mapOrder(sorter.order));
                    }
                }}
            />

            <Pagination
                current={page}
                pageSize={limit}
                total={total}
                onChange={(p) => fetchJobs(p)}
                style={{ marginTop: 16, textAlign: 'right' }}
            />

            <Modal
                open={open}
                onCancel={() => { setOpen(false); setEditData(null); }}
                footer={null}
                destroyOnClose
            >
                <CareerForm initialValues={editData} onSubmit={handleSubmit} editMode={!!editData} />
            </Modal>

            <Modal
                title="Job Detail"
                open={openDetail}
                width={1000}
                onCancel={() => setOpenDetail(false)}
                footer={[
                    <Button
                        key="close"
                        style={{ backgroundColor: '#ec4899', borderColor: '#ec4899', color: '#fff' }}
                        onClick={() => setOpenDetail(false)}
                    >
                        Close
                    </Button>
                ]}
            >
                {detailData && (
                    <Table
                        dataSource={detailOrder.map((key) => {
                            let value = detailData[key];
                            if (key === "category" && value?.name) value = value.name;
                            if ((key === "created_at" || key === "updated_at") && value) {
                                value = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
                            }
                            if ((key === "description" || key === "qualification") && value) {
                                let items = value;
                                if (typeof value === "string") {
                                    try { items = JSON.parse(value); } catch { items = value; }
                                }
                                if (Array.isArray(items)) {
                                    value = (
                                        <ul style={{ paddingLeft: 16, margin: 0, listStyleType: "disc", wordBreak: "break-word" }}>
                                            {items.map((item, idx) => <li key={idx}>{item}</li>)}
                                        </ul>
                                    );
                                }
                            }
                            return { key, field: fieldLabels[key] || key, value };
                        })}
                        columns={[
                            { title: 'Field', dataIndex: 'field', key: 'field', width: '30%', render: text => <strong>{text}</strong> },
                            { title: 'Value', dataIndex: 'value', key: 'value' },
                        ]}
                        pagination={false}
                        bordered
                    />
                )}
            </Modal>
        </Card>
    );
};

export default Career;
